/**
 * 路由语言辅助函数
 * 用于在页面跳转时保持语言参数
 */

import { getLanguageFromURL, isEnglish } from '@/i18n/utils';

/**
 * 生成带语言参数的查询对象
 * @param {Object} currentQuery - 当前的查询参数对象
 * @returns {Object} 包含语言参数的查询对象
 */
export function getLanguageQuery(currentQuery = {}) {
  const query = { ...currentQuery };
  
  // 只有在英文模式下才添加 lang=en 参数
  // 中文模式下不添加参数（默认行为）
  if (isEnglish()) {
    query.lang = 'en';
  }
  
  return query;
}

/**
 * 获取当前语言的路由参数字符串
 * @param {Object} currentQuery - 当前的查询参数对象
 * @returns {string} URL查询字符串
 */
export function getLanguageQueryString(currentQuery = {}) {
  const query = getLanguageQuery(currentQuery);
  const params = new URLSearchParams();
  
  Object.keys(query).forEach(key => {
    if (query[key] !== undefined && query[key] !== null) {
      params.append(key, query[key]);
    }
  });
  
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * 为路由对象添加语言参数
 * @param {Object} routeObject - Vue Router 路由对象
 * @returns {Object} 包含语言参数的路由对象
 */
export function addLanguageToRoute(routeObject) {
  if (!routeObject) {
    return routeObject;
  }
  
  // 如果是字符串路径，转换为对象
  if (typeof routeObject === 'string') {
    return {
      path: routeObject,
      query: getLanguageQuery()
    };
  }
  
  // 如果是对象，合并语言参数到 query 中
  return {
    ...routeObject,
    query: getLanguageQuery(routeObject.query)
  };
}

/**
 * 检查当前路由是否包含正确的语言参数
 * @param {Object} route - Vue Router 路由对象
 * @returns {boolean} 是否包含正确的语言参数
 */
export function hasCorrectLanguageParam(route) {
  const currentLang = getLanguageFromURL();
  const routeLang = route.query?.lang;
  
  // 英文模式下应该有 lang=en 参数
  if (currentLang === 'en-US') {
    return routeLang === 'en';
  }
  
  // 中文模式下不应该有 lang 参数，或者参数不是 'en'
  return !routeLang || routeLang !== 'en';
}

/**
 * 带语言参数的导航方法
 * 确保跳转时保持当前语言设置
 * @param {Object} router - Vue Router 实例
 * @param {Object|string} to - 目标路由
 * @returns {Promise} 导航 Promise
 */
export function navigateWithLanguage(router, to) {
  if (!router) {
    console.error('Router instance is required for navigateWithLanguage');
    return Promise.reject(new Error('Router instance is required'));
  }
  
  const routeWithLanguage = addLanguageToRoute(to);
  return router.push(routeWithLanguage);
}

/**
 * 带语言参数的替换导航方法
 * 确保替换时保持当前语言设置
 * @param {Object} router - Vue Router 实例
 * @param {Object|string} to - 目标路由
 * @returns {Promise} 导航 Promise
 */
export function replaceWithLanguage(router, to) {
  if (!router) {
    console.error('Router instance is required for replaceWithLanguage');
    return Promise.reject(new Error('Router instance is required'));
  }
  
  const routeWithLanguage = addLanguageToRoute(to);
  return router.replace(routeWithLanguage);
}

/**
 * Vue 组件 mixin，提供语言保持的导航方法
 * 可以在组件中使用 this.navigateWithLanguage() 和 this.replaceWithLanguage()
 */
export const routeLanguageMixin = {
  methods: {
    /**
     * 带语言参数的导航
     * @param {Object|string} to - 目标路由
     * @returns {Promise} 导航 Promise
     */
    navigateWithLanguage(to) {
      return navigateWithLanguage(this.$router, to);
    },
    
    /**
     * 带语言参数的替换导航
     * @param {Object|string} to - 目标路由
     * @returns {Promise} 导航 Promise
     */
    replaceWithLanguage(to) {
      return replaceWithLanguage(this.$router, to);
    },
    
    /**
     * 获取带语言参数的查询对象
     * @param {Object} currentQuery - 当前查询参数
     * @returns {Object} 包含语言参数的查询对象
     */
    getLanguageQuery(currentQuery) {
      return getLanguageQuery(currentQuery);
    },
    
    /**
     * 为路由对象添加语言参数
     * @param {Object|string} routeObject - 路由对象
     * @returns {Object} 包含语言参数的路由对象
     */
    addLanguageToRoute(routeObject) {
      return addLanguageToRoute(routeObject);
    }
  },
  
  computed: {
    /**
     * 当前路由的语言查询参数
     * @returns {Object} 包含语言参数的查询对象
     */
    routeQuery() {
      return getLanguageQuery(this.$route.query);
    }
  }
};